import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Icon,
  VStack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { changePassword } from "api/api";

export default function ChangePasswordModal({ isOpen, onClose }) {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    ancienMotDePasse: "",
    nouveauMotDePasse: "",
    confirmationMotDePasse: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.ancienMotDePasse) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer votre ancien mot de passe",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.nouveauMotDePasse) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer votre nouveau mot de passe",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (formData.nouveauMotDePasse.length < 8) {
      toast({
        title: "Erreur",
        description: "Le nouveau mot de passe doit contenir au moins 8 caractères",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (formData.nouveauMotDePasse !== formData.confirmationMotDePasse) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (formData.ancienMotDePasse === formData.nouveauMotDePasse) {
      toast({
        title: "Erreur",
        description: "Le nouveau mot de passe doit être différent de l'ancien",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await changePassword(formData.ancienMotDePasse, formData.nouveauMotDePasse);

      toast({
        title: "Succès",
        description: "Mot de passe changé avec succès",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Réinitialiser le formulaire et fermer la modal
      setFormData({
        ancienMotDePasse: "",
        nouveauMotDePasse: "",
        confirmationMotDePasse: "",
      });
      setShowPasswords({ old: false, new: false, confirm: false });
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Erreur lors du changement de mot de passe";

      if (error.response?.status === 401) {
        toast({
          title: "Erreur",
          description: "Ancien mot de passe incorrect",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Erreur",
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      console.error("Erreur changement mot de passe:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Réinitialiser le formulaire à la fermeture
    setFormData({
      ancienMotDePasse: "",
      nouveauMotDePasse: "",
      confirmationMotDePasse: "",
    });
    setShowPasswords({ old: false, new: false, confirm: false });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent borderRadius="lg" boxShadow="xl">
        <ModalHeader fontSize="xl" fontWeight="700" color="gray.900">
          Changer votre mot de passe
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5}>
            <Text fontSize="sm" color="gray.600">
              Entrez votre ancien mot de passe et votre nouveau mot de passe pour mettre à jour votre compte.
            </Text>

            {/* Ancien mot de passe */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.800">
                Ancien mot de passe
              </FormLabel>
              <InputGroup>
                <Input
                  name="ancienMotDePasse"
                  type={showPasswords.old ? "text" : "password"}
                  placeholder="Entrez votre ancien mot de passe"
                  value={formData.ancienMotDePasse}
                  onChange={handleInputChange}
                  h="48px"
                  borderRadius="lg"
                  isDisabled={isLoading}
                />
                <InputRightElement height="48px">
                  <Icon
                    as={showPasswords.old ? MdVisibility : MdVisibilityOff}
                    cursor="pointer"
                    color="gray.400"
                    onClick={() => togglePasswordVisibility("old")}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Nouveau mot de passe */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.800">
                Nouveau mot de passe
              </FormLabel>
              <InputGroup>
                <Input
                  name="nouveauMotDePasse"
                  type={showPasswords.new ? "text" : "password"}
                  placeholder="Entrez votre nouveau mot de passe (min. 8 caractères)"
                  value={formData.nouveauMotDePasse}
                  onChange={handleInputChange}
                  h="48px"
                  borderRadius="lg"
                  isDisabled={isLoading}
                />
                <InputRightElement height="48px">
                  <Icon
                    as={showPasswords.new ? MdVisibility : MdVisibilityOff}
                    cursor="pointer"
                    color="gray.400"
                    onClick={() => togglePasswordVisibility("new")}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Confirmation mot de passe */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.800">
                Confirmez le mot de passe
              </FormLabel>
              <InputGroup>
                <Input
                  name="confirmationMotDePasse"
                  type={showPasswords.confirm ? "text" : "password"}
                  placeholder="Confirmez votre nouveau mot de passe"
                  value={formData.confirmationMotDePasse}
                  onChange={handleInputChange}
                  h="48px"
                  borderRadius="lg"
                  isDisabled={isLoading}
                />
                <InputRightElement height="48px">
                  <Icon
                    as={showPasswords.confirm ? MdVisibility : MdVisibilityOff}
                    cursor="pointer"
                    color="gray.400"
                    onClick={() => togglePasswordVisibility("confirm")}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button
            variant="ghost"
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            bg="#006133"
            color="white"
            onClick={handleSubmit}
            isLoading={isLoading}
            _hover={{ bg: "#004d26" }}
          >
            Changer le mot de passe
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
